import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { BannerNotificationsService, LocalStorageService, SanitizeService } from '../../xform-compat';
import {
  AuthenticationService,
  AuthorizationService,
  ConnectionService,
  PermissionsChangedLogoutService,
  RmsUtilsService,
  GoogleTagManagerService
} from '../../core/services';
import { CONFIG } from '../../config';
import { User, ItemMenu } from '../../shared/models';
import { MENU } from '../../shared/menu';
import { PERMISSIONS } from '../../shared/enums/permissions.enum';
import { ANALYTICS_EVENTS } from '../../shared/enums/analytics-event.enum';
import { AUTH_ROUTES } from '../../shared/constants/auth-routes';
import { EventsHubService } from '../../core/events-hub.service';
import { HeartbeatService, PortfolioService, SystemSettingsService } from '../../core/data-services';
import { LinkService } from '../../core/data-services/link.service';

@Component({
  selector: 'xform-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false
})
export class HeaderComponent implements OnDestroy, OnInit, AfterViewInit {
  @ViewChild('navList') navListRef: ElementRef;
  @ViewChild('headerLogo') headerLogoRef: ElementRef;
  @ViewChild('headerActions') headerActionsRef: ElementRef;

  private readonly LAST_HEARTBEAT_KEY = 'lastHearBeat';
  private readonly HEARTBEAT_SECONDS = 15;
  private heartBeat: Subscription;
  private subscriptionPortfolio: Subscription;
  private resizeObserver: ResizeObserver;
  private navCollapseCheckTimeout: any;
  private subscriptions: Array<Subscription> = [];
  private storageEventListener: (e: StorageEvent) => void;
  private visibilityChangeListener: () => void;
  private marketStatusTimeout: any;

  public loggedIn: boolean;
  public loggedUser: User;
  public defaultProfileImage: string;
  public menu: Array<ItemMenu>;
  public PERMISSIONS = PERMISSIONS;
  public searchPortalClosedByQuickSearchInteraction: boolean;
  public searchTerm: string;
  public isQuickSearchOpen: boolean;

  /** Whether the main nav should be collapsed (moved to side panel) */
  public isNavCollapsed = false;

  /** Whether the side panel is currently open */
  public isSidePanelOpen = false;

  /** Tracks the breakpoint width at which nav items start wrapping */
  private navCollapseBreakpoint = 0;

  /** Flag to track if menu has been loaded at least once */
  private menuLoaded = false;

  /**
   * RA-2808: the favicon is now a fixed per-environment marker, so the market open/close
   * swap is switched off. Kept (not removed) in case it comes back; flip to true to re-enable.
   */
  private readonly marketStatusIconEnabled = false;
  private readonly iconsUrls = {
    marketOpen: 'assets/images/favicon-open.png',
    marketClose: 'assets/images/favicon.png'
  };
  favIcon: HTMLLinkElement = document.querySelector('#favicon');

  @HostListener('window:resize')
  onResize(): void {
    this.scheduleNavCollapseCheck();
  }

  constructor(
    private authenticationService: AuthenticationService,
    private sanitizeService: SanitizeService,
    private eventsHubService: EventsHubService,
    private authorizationService: AuthorizationService,
    private heartbeatService: HeartbeatService,
    private bannerNotificationService: BannerNotificationsService,
    private connectionService: ConnectionService,
    private localStorageService: LocalStorageService,
    private portfolioService: PortfolioService,
    private rmsUtilsService: RmsUtilsService,
    private router: Router,
    private permissionsChangedLogoutService: PermissionsChangedLogoutService,
    private systemSettingsService: SystemSettingsService,
    private cdref: ChangeDetectorRef,
    private googleTagManagerService: GoogleTagManagerService,
    private linkService: LinkService
  ) {
    this.subscriptionPortfolio = this.portfolioService.getMessage().subscribe(message => {
      if (message.text === 'updated') {
        this.loadLoggedUserData();
      }
    });
    this.defaultProfileImage = CONFIG.defaultProfileImage;

    // Track subscriptions for cleanup
    this.subscriptions.push(
      this.eventsHubService.profileUpdated$.subscribe(() => this.loadLoggedUserData()),
      this.eventsHubService.stopHeartBeat$.subscribe(() => this.unsubscribeHeartBeat())
    );

    this.isQuickSearchOpen = false;
    this.searchPortalClosedByQuickSearchInteraction = false;
    this.searchTerm = '';
  }

  ngOnInit(): void {
    this.loggedIn = this.authenticationService.isLoggedIn();

    this.subscriptions.push(
      this.eventsHubService.isLoggedIn$.subscribe((loggedIn) => {
        this.loggedIn = loggedIn;
        this.loadLoggedUserData();
      }),
      this.connectionService.monitor().subscribe(value => {
        if (value && this.loggedIn) {
          this.startHeartBeat();
        } else if (this.heartBeat) {
          this.heartBeat.unsubscribe();
        }
      })
    );

    this.loadLoggedUserData();
    this.localStorageListener();
    this.routerNavigationListener();
    this.setupVisibilityListener();
  }

  ngAfterViewInit(): void {
    this.setupNavCollapseDetection();
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.subscriptionPortfolio?.unsubscribe();
    this.subscriptions.forEach(sub => sub?.unsubscribe());
    this.heartBeat?.unsubscribe();

    // Clean up observers
    this.resizeObserver?.disconnect();

    // Clean up timeouts
    if (this.navCollapseCheckTimeout) {
      clearTimeout(this.navCollapseCheckTimeout);
    }
    if (this.marketStatusTimeout) {
      clearTimeout(this.marketStatusTimeout);
    }

    // Remove storage event listener
    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
    }

    // Remove visibility change listener
    if (this.visibilityChangeListener) {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    }
  }

  /**
   * Sets up detection for when nav items wrap to a new line.
   * Uses ResizeObserver to detect changes in nav list dimensions.
   */
  private setupNavCollapseDetection(): void {
    // Set up ResizeObserver to watch for nav list size changes
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleNavCollapseCheck();
      });
    }
  }

  /**
   * Schedules a nav collapse check with debouncing to avoid excessive calls
   */
  private scheduleNavCollapseCheck(): void {
    if (this.navCollapseCheckTimeout) {
      clearTimeout(this.navCollapseCheckTimeout);
    }
    this.navCollapseCheckTimeout = setTimeout(() => this.checkNavCollapse(), 50);
  }

  /**
   * Checks if the navigation items would overflow and updates isNavCollapsed accordingly.
   * Compares the total required width against available viewport width.
   */
  private checkNavCollapse(): void {
    // Don't check until menu is loaded and all refs are available
    if (!this.menuLoaded || !this.navListRef?.nativeElement ||
        !this.headerLogoRef?.nativeElement || !this.headerActionsRef?.nativeElement) {
      return;
    }

    const navList = this.navListRef.nativeElement as HTMLElement;
    const logo = this.headerLogoRef.nativeElement as HTMLElement;
    const actions = this.headerActionsRef.nativeElement as HTMLElement;
    const navContainer = navList.parentElement as HTMLElement;

    if (!navContainer) {
      return;
    }

    // Get fixed widths (logo and actions)
    const logoWidth = logo.offsetWidth;
    const actionsWidth = actions.offsetWidth;

    // Get padding (25px on each side, or 15px on smaller screens)
    const headerPadding = window.innerWidth <= 992 ? 30 : 50; // 15px * 2 or 25px * 2

    // Get nav margin (20px on each side)
    const navMargin = 40;

    // Calculate available space for nav
    const availableWidth = window.innerWidth - logoWidth - actionsWidth - headerPadding - navMargin;

    // Get the nav's natural width (scrollWidth gives full content width)
    // We need to temporarily show the nav to measure it if it's hidden
    const wasHidden = this.isNavCollapsed;
    if (wasHidden) {
      // Temporarily make nav visible off-screen to measure
      navContainer.style.visibility = 'visible';
      navContainer.style.width = 'auto';
      navContainer.style.position = 'absolute';
      navContainer.style.left = '-9999px';
      navContainer.style.margin = '0';
    }

    // Get the actual width needed by nav items
    const navWidth = navList.scrollWidth;

    // Restore hidden state if it was hidden
    if (wasHidden) {
      navContainer.style.visibility = '';
      navContainer.style.width = '';
      navContainer.style.position = '';
      navContainer.style.left = '';
      navContainer.style.margin = '';
    }

    // Check if nav would overflow
    const wouldOverflow = navWidth > availableWidth;

    if (wouldOverflow) {
      // Nav would overflow, update breakpoint and collapse
      this.navCollapseBreakpoint = Math.max(this.navCollapseBreakpoint, window.innerWidth);
      this.isNavCollapsed = true;
    } else {
      // Nav fits, check if we're above the breakpoint to expand
      this.isNavCollapsed = window.innerWidth <= this.navCollapseBreakpoint;
    }

    this.cdref.detectChanges();
  }

  /**
   * Handles event from menu item without link
   */
  public menuEventHubServiceHandler(item: string): void {
    if (item && this.eventsHubService[item]) {
      this.eventsHubService[item]();
    }
  }

  /**
   * Handles a click on a submenu item with a link. Pushes an analytics event for
   * tracked items so usage can be measured via Google Analytics.
   */
  public onSubMenuItemClick(subItem: ItemMenu): void {
    if (subItem?.textKey === 'MENU.BLACKBIRD') {
      this.googleTagManagerService.trackEvent(ANALYTICS_EVENTS.BlackbirdNavClick);
    }
  }

  private loadLoggedUserData(): void {
    if (this.loggedIn) {
      if (this.marketStatusIconEnabled) {
        this._setMarketStatusIcon(this.systemSettingsService, this);
      }
      this.loggedUser = this.authenticationService.getLoggedUser();
      this.menu = this.authorizationService.filterMenu(MENU, true).map(item => {
        item.open = false;
        if (item.subMenu) {
          item.subMenu = this.authorizationService.filterMenu(item.subMenu);
        }
        if (item.textKey === 'MENU.PORTFOLIO') {
          item.subMenu = [];
          this.portfolioService.getPortfolios().subscribe(
            portfolios => {
              portfolios.records.forEach(portfolio => {
                item.subMenu.push({
                  textKey: portfolio.name,
                  link: '/portfolio/' + portfolio.id,
                  icon: '',
                  permissionsNeeded: []
                });
              });
              // Recheck after async data loads
              this.scheduleNavCollapseCheck();
            },
            error => this.bannerNotificationService.error(error)
          );
        } else if (item.textKey === 'MENU.QRS_LINKS') {
          item.subMenu = [];
          this.linkService.getRecords().subscribe(result => {
            result.records.forEach(link => {
              const include = link.permissionRequired === null ||
                this.authorizationService.hasPermission([`QRS_LINKS_${link.permissionRequired}_READ`]);
              if (include) {
                item.subMenu.push({
                  textKey: link.description,
                  link: link.href,
                  icon: '',
                  permissionsNeeded: []
                });
              }
            });
            // Recheck after async data loads
            this.scheduleNavCollapseCheck();
          });
        }
        return item;
      });
      this.startHeartBeat();
      if (this.loggedUser) {
        this.loggedUser.profileImageBase64 = this.sanitizeService.sanitizeBase64Image(this.loggedUser.profileImage);
      }

      // Mark menu as loaded and start observing after a short delay for DOM to render
      this.menuLoaded = true;
      setTimeout(() => {
        if (this.navListRef?.nativeElement && this.resizeObserver) {
          this.resizeObserver.observe(this.navListRef.nativeElement);
        }
        this.checkNavCollapse();
      }, 100);
    }
  }

  /**
   * Creates a listener for the local storage to watch changes in the user value.
   */
  private localStorageListener(): void {
    this.storageEventListener = (e: StorageEvent) => {
      // localStorage.clear() in another tab fires a single event with no key; the session is gone, so follow it out
      if (e.key === null && e.storageArea === localStorage) {
        this.followLogout();
        return;
      }
      if (e.key === 'rms-loggedUser') {
        this.handleLoggedUserWrite(e.newValue, e.oldValue);
      }
      if ((e.key === 'rms-tokenInfo') && e.newValue && !e.oldValue) {
        this.loggedIn = true;
        this.router.navigate([this.rmsUtilsService.loadLastNavigatedPage()]);
      }
    };
    window.addEventListener('storage', this.storageEventListener);
  }

  /**
   * Reacts to another tab writing the stored user. The baseline is the event's own previous value, i.e. what was
   * stored just before this write no matter which tab wrote it - a copy kept by this tab would go stale, because a
   * tab never receives storage events for its own writes (e.g. its own logout and re-login).
   * <ul>
   *   <li>User cleared (logout, or unreadable JSON): this tab logs out too.</li>
   *   <li>User written over nothing (a login from another tab): this tab is joining that session, so it adopts the
   *   user and reloads what it displays. It is not compared against any earlier user, which would force-logout
   *   the fresh session.</li>
   *   <li>Otherwise the permissions are compared, failing closed (forced logout) when the user is malformed or the
   *   comparison cannot be made (RA-2262).</li>
   * </ul>
   */
  private handleLoggedUserWrite(rawValue: string | null, rawPreviousValue: string | null): void {
    const newLoggedUserInfo = this.parseStoredUser(rawValue);
    const baseline = this.parseStoredUser(rawPreviousValue);

    if (!newLoggedUserInfo) {
      this.followLogout();
      return;
    }
    // The shared token is the session's source of truth; this tab's loggedIn flag can lag behind other tabs' writes
    if (!this.authenticationService.isLoggedIn()) {
      return;
    }
    // A previous value that exists but cannot be read is not "nothing", so it fails closed rather than being adopted
    const baselineUnreadable = !!rawPreviousValue && !baseline;
    if (!this.hasPermissionShape(newLoggedUserInfo) || baselineUnreadable) {
      this.permissionsChangedLogoutService.promptAndLogout();
    } else if (!baseline) {
      this.loggedIn = true;
      this.loadLoggedUserData();
    } else if (this.permissionsDiffer(baseline, newLoggedUserInfo)) {
      this.permissionsChangedLogoutService.promptAndLogout();
    }
  }

  /**
   * Logs this tab out after another tab ended the session. Either this tab's flag or the shared token counts as
   * logged in, because the flag can lag behind other tabs' writes and the token may already be gone.
   */
  private followLogout(): void {
    if (this.loggedIn || this.authenticationService.isLoggedIn()) {
      this.logout();
    }
  }

  private parseStoredUser(rawValue: string | null): User | null {
    try {
      return rawValue ? JSON.parse(rawValue) : null;
    } catch {
      return null;
    }
  }

  /**
   * Whether the user carries the userTenants -> tenantRoles structure that permission checks read.
   */
  private hasPermissionShape(user: User): boolean {
    const tenant = Array.isArray(user?.userTenants) ? user.userTenants[0] : null;
    return !!tenant && Array.isArray(tenant.tenantRoles);
  }

  /**
   * Compares permissions, treating a comparison that cannot be made (e.g. a malformed baseline) as a change.
   */
  private permissionsDiffer(baseline: User, newLoggedUserInfo: User): boolean {
    try {
      return this.authenticationService.permissionsChanged(baseline, newLoggedUserInfo);
    } catch {
      return true;
    }
  }

  /**
   * Tracks navigated pages and stores history for redirect after login.
   */
  private routerNavigationListener(): void {
    this.subscriptions.push(
      this.router.events.subscribe((val) => {
        if (val instanceof NavigationEnd) {
          // ⚠️ '/' combined with `.includes(path)` below matches every URL, so shouldStore is always false:
          // neither key is ever written (these are their only writers), and loadLastNavigatedPage therefore
          // always returns /dashboard. Left as-is deliberately — fixing it changes the post-login redirect.
          const excludedPaths = ['/', ...AUTH_ROUTES];
          const shouldStore = !excludedPaths.some(path =>
            val.urlAfterRedirects === path || val.urlAfterRedirects.includes(path)
          );
          if (shouldStore) {
            localStorage.setItem('lastNavigatedItemLS', val.urlAfterRedirects);
            sessionStorage.setItem('lastNavigatedItemSS', val.urlAfterRedirects);
          }
        }
      })
    );
  }

  /**
   * Registers a visibilitychange listener so that when iOS (or any browser) brings the app
   * back to the foreground, an immediate heartbeat is sent and the heartbeat interval is reset.
   * This prevents the server from evicting the session due to a stale lastHeartbeat timestamp
   * accumulated while the app was suspended in the background.
   */
  private setupVisibilityListener(): void {
    this.visibilityChangeListener = () => {
      if (document.visibilityState === 'visible' && this.loggedIn) {
        this.localStorageService.set(this.LAST_HEARTBEAT_KEY, new Date().getTime());
        this.heartbeatService.get().subscribe(() => {});
        this.unsubscribeHeartBeat();
        this.startHeartBeat();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeListener);
  }

  /**
   * Time interval to check the heartbeat.
   */
  private startHeartBeat(): void {
    if (this.heartBeat) {
      this.heartBeat.unsubscribe();
    }
    this.heartBeat = interval(this.HEARTBEAT_SECONDS * 1000).subscribe(() => {
      const lastHearBeat = this.localStorageService.get(this.LAST_HEARTBEAT_KEY) || 0;
      const currentTimestamp = new Date().getTime();
      const timeDiff = currentTimestamp - lastHearBeat;
      const loggedIn = this.authenticationService.isLoggedIn();

      if ((timeDiff >= this.HEARTBEAT_SECONDS * 1000) && loggedIn) {
        this.localStorageService.set(this.LAST_HEARTBEAT_KEY, currentTimestamp);
        this.heartbeatService.get().subscribe(() => {});
      }
    });
  }

  public logout(): void {
    this.unsubscribeHeartBeat();
    this.authenticationService.logout();
  }

  /**
   * Handles side panel toggle events
   */
  public onSidePanelToggle(isOpen: boolean): void {
    this.isSidePanelOpen = isOpen;
    // Close all menu accordions when panel closes
    if (!isOpen && this.menu) {
      this.menu.forEach(item => item.open = false);
    }
  }

  /**
   * Receives the updated search term
   */
  public searchInputDetected(term: string): void {
    this.searchTerm = term;
  }

  public quickSearchOpen(status: boolean): void {
    this.isQuickSearchOpen = status;
    this.isSidePanelOpen = false;
    this.searchPortalClosedByQuickSearchInteraction = status;
  }

  /**
   * Closes the search portal
   */
  public closeSearchPortal(): void {
    this.searchPortalClosedByQuickSearchInteraction = true;
  }

  /**
   * Checks if a link is external (starts with http)
   */
  public isExternalLink(link: string): boolean {
    return link?.startsWith('http');
  }

  /**
   * Unsubscribes heartbeat when it must be stopped.
   */
  public unsubscribeHeartBeat(): void {
    if (this.heartBeat) {
      this.heartBeat.unsubscribe();
    }
  }

  private _setMarketStatusIcon(service: SystemSettingsService, that: HeaderComponent): void {
    service.getMarketSettings().subscribe(status => {
      const nextCheckIn = (status.nextChangeIn > 1800 ? 1800 : status.nextChangeIn) * 1000;
      const open = status.isOpen;
      if (that.favIcon) {
        that.favIcon.href = open ? that.iconsUrls.marketOpen : that.iconsUrls.marketClose;
      }
      that.marketStatusTimeout = setTimeout(() => that._setMarketStatusIcon(service, that), nextCheckIn + 100);
    });
  }
}
